import { Routes, Route, Navigate } from "react-router-dom";
import { RequireAuth } from "./auth/RequireAuth";
import { Login } from "./views/Login";
import { Signup } from "./views/Signup";
import { Dashboard } from "./views/Dashboard";
import { Graph } from "./views/Graph";
import { NewIdea } from "./views/NewIdea";
import { Product } from "./views/Product";
import { Review } from "./views/Review";
import { Settings } from "./views/Settings";
import { Changes } from "./views/Changes";
import { Privacy } from "./views/legal/Privacy";
import { Terms } from "./views/legal/Terms";
import { Contact } from "./views/legal/Contact";
import { KeyboardCheatSheet } from "./components/KeyboardCheatSheet";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { ActiveRunProvider } from "./components/ActiveRunProvider";
import { RunToast } from "./components/RunToast";
import { RunCompletionNotifier } from "./components/RunCompletionNotifier";
import { useGlobalShortcuts } from "./hooks/useGlobalShortcuts";
import { useThemeSync } from "./auth/useThemeSync";
import { useNotificationSync } from "./hooks/useNotificationSync";

export function App() {
  // Global keyboard shortcuts (g d → dashboard, g g → product graph,
  // ? → cheat sheet). Hook gates on text-input focus internally so it
  // doesn't hijack typing in textareas / inputs.
  useGlobalShortcuts();
  // Hydrate the theme store from the server's User.theme_preference
  // once /me resolves. Per-uid tracking re-hydrates after sign-out +
  // sign-in-as-different-user.
  useThemeSync();
  // Hydrate notification prefs from /me (0027 P6).
  useNotificationSync();

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/new"
          element={
            <RequireAuth>
              <NewIdea />
            </RequireAuth>
          }
        />
        <Route
          path="/products/:productId"
          element={
            <RequireAuth>
              <Product />
            </RequireAuth>
          }
        />
        <Route
          path="/products/:productId/review"
          element={
            <RequireAuth>
              <Review />
            </RequireAuth>
          }
        />
        <Route
          path="/products/:productId/graph"
          element={
            <RequireAuth>
              <Graph />
            </RequireAuth>
          }
        />
        <Route
          path="/products/:productId/changes"
          element={
            <RequireAuth>
              <Changes />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        {/* Legal pages — public, no RequireAuth (founders may want to
            read these before signing in). */}
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/legal/contact" element={<Contact />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Keyboard cheat sheet — opens via the `?` shortcut, rendered
          outside <Routes> so it overlays whatever route is active. */}
      <KeyboardCheatSheet />
      {/* Impersonation banner (0016 Slice 3) — sticky red bar at the
          top of every page when the admin is impersonating this user. */}
      <ImpersonationBanner />
      {/* Owns in-flight run SSE streams app-wide (0027 P1) so they survive
          navigation; feeds the global run store the menu/recap read from. */}
      <ActiveRunProvider />
      {/* In-app completion toast (0027) — fires when a run finishes/fails
          while the tab is focused. Persistent awareness lives in the header
          RunMenu; off-site surfaces (browser notification + sound) below. */}
      <RunToast />
      {/* Off-site completion surfaces — browser notification + sound (0027 P6). */}
      <RunCompletionNotifier />
    </>
  );
}
